import isEmpty from 'lodash/isEmpty';
import { nextTick } from 'vue';
import { type PageView3Params, QAnalyticsClient } from 'q-analytics';
import $store from './StoreService';
import LocalStorage from './LocalStorage';

import { isProd, isServer } from '~/utils/EnvUtils';
import featureFlags from '~/services/FeatureFlagsService';
import type { RouteLocation } from 'vue-router';
import QLogger from './QLogger';

interface User {
  userId: string,
  subPlanId?: string,
  occupation: string,
  country: string,
  media: string,
  locale: string,
  paidOrganization?: string,
  subscriptions?: Record<string, any>[],
}

enum EventType {
  click = 'click',
  impression = 'impression',
  modalView = 'modalView',
  hover = 'hover',
}

const areDevEventsAllowed = !!LocalStorage.getItem('QDevEvents');

// INITIALIZE ANALYTICS CLIENT
const QAnalyticsClientInstance = QAnalyticsClient.getInstance({
  isProd: isProd(),
  areDevEventsAllowed,
});

class EventLoggerService {
  wasReferred: boolean = false;

  oldEventLoggerInstance: any;

  getState: () => any;

  getUserSubsPlanId: (user: {
    subscriptions: string | any[],
  }) => any;

  constructor() {
    this.getState = function getState() {
      const store = $store();
      return { ...store.state.root, analyticsManager: store.state.analyticsManager };
    };

    this.getUserSubsPlanId = function getUserSubsPlanId(user: { subscriptions: string | any[] }) {
      if (user && user.subscriptions && user.subscriptions.length) {
        const activeSub = user.subscriptions[0];
        const planId = activeSub.planId || activeSub.id;
        return planId;
      }
      return null;
    };
  }

  prepareEventData(eventName: string, params: Record<string, any>): Record<string, any> | void {
    // early return for ssr
    if (isServer()) {
      return;
    }
    // kill switch for new analytics client

    const additionalParams = this.getAdditionalParams();
    const enrichmentParams = this.getEnrichmentParams();

    if (isEmpty(eventName)) {
      QLogger.error(`Error at logEvent: given eventName(${eventName}) is not valid`, params);
      return;
    }

    const eventData = {
      params: {
        ...additionalParams,
        ...enrichmentParams,
      },
      optionalParams: params,
      featureFlags: featureFlags() ? featureFlags().getFeatureValues() : undefined,
    };
    return eventData;
  }

  logEvent(eventName: string, params: Record<string, any> = {}) {
    this.logAnalyticsEvent(eventName, params);
  }

  private logAnalyticsEvent(eventName: string, params: Record<string, any>, logV2Event = false) {
    const eventData = this.prepareEventData(eventName, params);

    if (!eventData || isEmpty(eventData)) {
      return;
    }

    if (logV2Event) {
      eventData.enableFrontendEvent = true;
    }

    QAnalyticsClientInstance.logEvent(eventName, eventData as Record<string, any>);

    const store = $store();
    nextTick(() => {
      store.dispatch('analyticsManager/setAnalyticsData', eventData);
    });
  }

  logClickEvent(eventName: string, params: Record<string, any> = {}) {
    params.eventType = EventType.click;
    this.logAnalyticsEvent(eventName, params, true);
  }

  logImpressionEvent(eventName: string, params: Record<string, any> = {}) {
    params.eventType = EventType.impression;
    this.logAnalyticsEvent(eventName, params, true);
  }

  logModalViewEvent(eventName: string, params: Record<string, any> = {}) {
    params.eventType = EventType.modalView;
    this.logAnalyticsEvent(eventName, params, true);
  }

  logHoverEvent(eventName: string, params: Record<string, any> = {}) {
    params.eventType = EventType.impression;
    this.logAnalyticsEvent(eventName, params, true);
  }

  processPageViewEvents(
    currentUrl: RouteLocation,
    previousUrl: RouteLocation,
    query: Record<string, any>,
    source = '',
  ) {
    // early return for ssr
    if (isServer()) {
      return;
    }
    // kill switch for pageview3

    let isReferred = false;
    if (document.referrer !== '' && !this.wasReferred) {
      isReferred = true;
    }
    const isBrowserLoaded = previousUrl && previousUrl.path === '/' && isEmpty(previousUrl.query);

    const state = this.getState();
    let url = '';

    if (isReferred) {
      url = document.referrer;
    } else if (previousUrl && previousUrl.fullPath) {
      url = previousUrl.fullPath;
    }

    let referrerVariant = 'UNKNOWN';

    if (isBrowserLoaded && query.source) {
      referrerVariant = 'ExternalClick';
    }

    if (!query.source && isBrowserLoaded) {
      referrerVariant = 'BrowserLoad';
    }

    if (source || !isBrowserLoaded) {
      referrerVariant = 'SourceClick';
    }

    try {
      const options: PageView3Params = {
        locale: state.deviceInfo?.locale ? state.deviceInfo.locale : null,
        referrer: {
          sourceName: source,
          url,
          variant: referrerVariant,
        },
        region: state.serverData?.region ? state.serverData.region : null,
        requestCountry: state.serverData?.requestCountry ? state.serverData.requestCountry : null,
        screenType: state.deviceProps?.type ? state.deviceProps.type : null,
        userId: state.user?.id,
        featureFlags: featureFlags() ? featureFlags().getFeatureValues() : undefined,
      };

      QAnalyticsClientInstance.logPageView3Event(currentUrl, previousUrl, options);
      // log pageview2 along with pageview3
      if (!this.oldEventLoggerInstance) {
        import('./EventLoggerService_old').then((module) => {
          this.oldEventLoggerInstance = module.default;
          this.oldEventLoggerInstance.processPageViewEvents(currentUrl, previousUrl, query);
        });
      } else {
        this.oldEventLoggerInstance.processPageViewEvents(currentUrl, previousUrl, query);
      }
    } catch (error) {
      QLogger.error(error);
    }

    if (isReferred) {
      this.wasReferred = true;
    }
  }

  getAdditionalParams() {
    const state = this.getState();
    const addParams: User = {} as User;

    const { user } = state;

    addParams.userId = user?.id;
    addParams.subPlanId = this.getUserSubsPlanId(user);
    addParams.occupation = user?.occupation;
    addParams.country = state.serverData?.requestCountry ? state.serverData.requestCountry : null;
    addParams.media = state.deviceProps.type ? state.deviceProps.type : null;
    addParams.locale = state.deviceInfo.locale ? state.deviceInfo.locale : null;
    addParams.paidOrganization = user?.paidOrganization;
    return addParams;
  }

  getEnrichmentParams() {
    const state = this.getState();

    const { analyticsManager } = state;
    return analyticsManager.enrichmentData || {};
  }
}

const eventLogger = new EventLoggerService();

export default eventLogger;
